import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import useStore from "../store/useStore";
import { useAllShows } from "../hooks/useShows";
import Poster from "../components/Poster";
import LogCard from "../components/LogCard";
import NotificationBell from "../components/NotificationBell";
import FollowList from "../components/FollowList";
import { useAuth } from "../context/AuthContext";
import { getMyLogs, getMyLists } from "../lib/services";
import { lower } from "../lib/strings";

export default function Profile() {
  const { savedShows } = useStore();
  const allShows = useAllShows();
  const findShow = (id) => allShows.find((s) => s.id === id);
  const savedAsShows = savedShows.map(findShow).filter(Boolean);
  const { user, logout } = useAuth();

  const [logs, setLogs] = useState([]);
  const [lists, setLists] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [followListMode, setFollowListMode] = useState(null); // "followers" | "following" | null

  const refresh = useCallback(async () => {
    try {
      const [l, ls] = await Promise.all([getMyLogs(), getMyLists()]);
      setLogs(l.items || []);
      setLists(ls.items || []);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onLogDeleted = (deletedId) => {
    setLogs((arr) => arr.filter((it) => (it.log?.id || it.id) !== deletedId));
  };

  return (
    <main data-testid="profile-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <div className="flex items-baseline justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            diary
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell className="text-white/70 hover:text-acid" />
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="profile-admin-link"
                className="font-mono text-[10px] uppercase tracking-wider text-acid hover:underline"
              >
                admin
              </Link>
            )}
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="font-mono text-[10px] uppercase tracking-wider text-white/40 hover:text-acid inline-flex items-center gap-1"
            >
              <LogOut className="w-3 h-3" strokeWidth={2} />
              log out
            </button>
          </div>
        </div>
        <h1 className="font-display text-5xl leading-none mt-1">
          {user ? lower(user.displayName) || lower(user.username) || "diary" : "diary"}<span className="text-acid">.</span>
        </h1>
        <p
          data-testid="profile-handle"
          className="font-mono text-[11px] text-white/50 mt-2"
        >
          {user ? `@${user.username || ""}` : "your record of live performance."}
        </p>

        <div
          data-testid="profile-social-counts"
          className="mt-3 flex items-center gap-4 font-mono text-[11px] text-white/60"
        >
          <button
            type="button"
            data-testid="profile-following-tap"
            onClick={() => setFollowListMode("following")}
            className="hover:text-acid transition-colors"
          >
            <span data-testid="profile-following-count" className="text-white tabular-nums">
              {user?.followingCount ?? 0}
            </span>{" "}
            following
          </button>
          <span className="text-white/30">·</span>
          <button
            type="button"
            data-testid="profile-followers-tap"
            onClick={() => setFollowListMode("followers")}
            className="hover:text-acid transition-colors"
          >
            <span data-testid="profile-followers-count" className="text-white tabular-nums">
              {user?.followerCount ?? 0}
            </span>{" "}
            followers
          </button>
        </div>
      </header>

      {user?.username && (
        <FollowList
          open={!!followListMode}
          onClose={() => setFollowListMode(null)}
          username={user.username}
          mode={followListMode || "followers"}
        />
      )}

      {/* recent diary — primary */}
      <section className="px-5 py-6 border-b-2 border-white/15">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            recent
          </h2>
          <Link to="/log" className="font-mono text-[10px] uppercase tracking-wider text-acid">
            + new log
          </Link>
        </div>

        {!loaded ? (
          <p className="font-mono text-[11px] text-white/40">loading...</p>
        ) : logs.length === 0 ? (
          <div className="border border-white/15 p-5 text-center">
            <p className="font-display text-xl text-white/70 leading-tight">
              no logs yet.
            </p>
            <p className="font-mono text-[11px] text-white/40 mt-2">
              log a show you've seen.
            </p>
            <Link
              to="/log"
              data-testid="profile-empty-log-cta"
              className="mt-4 inline-block font-mono text-[11px] lowercase border-2 border-acid text-acid px-3 py-2 hover:bg-acid hover:text-black transition-colors"
            >
              log a show
            </Link>
          </div>
        ) : (
          <ul className="space-y-5" data-testid="diary-list">
            {logs.map((item) => {
              // /me/logs returns enriched envelopes: { log, show, user }.
              // tolerate the rare unenriched shape too (defensive — should
              // never happen in alpha, but keeps profile stable if a future
              // endpoint regresses).
              const log = item.log || item;
              const show = item.show || findShow(log.showId);
              return (
                <li
                  key={log.id || `log-${log.showId}`}
                  data-testid={`diary-log-${log.id}`}
                  className="border-b border-white/10 pb-5"
                >
                  <LogCard log={log} show={show} onDeleted={onLogDeleted} />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* saved strip */}
      <section className="px-5 py-5 border-b-2 border-white/15">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            saved ({savedAsShows.length})
          </h2>
          <Link to="/saved" className="font-mono text-[10px] uppercase tracking-wider text-acid">
            see all
          </Link>
        </div>
        {savedAsShows.length === 0 ? (
          <p className="font-mono text-[11px] text-white/40">nothing saved yet.</p>
        ) : (
          <div className="overflow-x-auto no-scrollbar -mx-5 px-5">
            <ul className="flex gap-3">
              {savedAsShows.map((s) => (
                <li key={s.id} className="shrink-0">
                  <Link to={`/show/${s.id}`}>
                    <Poster show={s} size="md" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* lists strip */}
      <section className="px-5 py-5 border-b-2 border-white/15">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            lists ({lists.length})
          </h2>
          <Link
            to="/lists?create=1"
            data-testid="profile-create-list-shortcut"
            className="font-mono text-[10px] uppercase tracking-wider text-acid"
          >
            + create list
          </Link>
        </div>
        {lists.length === 0 ? (
          <p className="font-mono text-[11px] text-white/40">
            no lists yet.{" "}
            <Link
              to="/lists?create=1"
              className="text-acid underline underline-offset-2"
            >
              start one
            </Link>
            .
          </p>
        ) : (
          <ul className="space-y-1.5">
            {lists.slice(0, 4).map((l) => (
              <li key={l.id}>
                <Link
                  to={`/list/${l.id}`}
                  className="flex items-baseline justify-between font-mono text-xs hover:text-acid transition-colors"
                >
                  <span className="truncate">{lower(l.title) || "untitled list"}</span>
                  <span className="text-white/40 text-[10px] ml-2 shrink-0">
                    {l.itemCount}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* compact stats */}
      <section data-testid="profile-stats" className="grid grid-cols-3 border-b border-white/10">
        <div className="px-3 py-3">
          <div className="font-display text-2xl leading-none text-acid tabular-nums">{logs.length}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 mt-0.5">logged</div>
        </div>
        <div className="px-3 py-3 border-l border-white/10">
          <div className="font-display text-2xl leading-none text-acid tabular-nums">{savedAsShows.length}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 mt-0.5">saved</div>
        </div>
        <div className="px-3 py-3 border-l border-white/10">
          <div className="font-display text-2xl leading-none text-acid tabular-nums">{lists.length}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 mt-0.5">lists</div>
        </div>
      </section>
    </main>
  );
}
